// 弾いた技の「得物の材質」と「攻撃力」(2026-10-14・パリィの音の作り直し ?parrySfx= が使う・src/parry_remake.ts)。
//   ユーザー原文 (10-14)「重さはボスの武器にしている材質や攻撃力に応じて変える感じで。大中小みたいな分け方ってよりは
//   もっとものによるかんじにしたほうがよさそう」。
//   材質 = 絵の設定 (docs/rules/asset-review.md から引く外見カード) の得物。重さ = 技の定義 (src/attacks.ts) の
//   体幹ダメージと HP ダメージから連続の値 0〜1 で出し、材質ごとの「物の重み」を少し足す。段 (軽・中・重) には切らない。
//   設計 = docs/research/2026-10-14-parry-sfx-remake.md
import { ATTACKS } from './attacks'

/** 弾いた得物の材質。音の「芯」(public/sfx/parry_remake/<案>_<材質>.wav) がこの単位で変わる。 */
export type ParryMaterial =
  | 'blade'       // 刀・細い剣 (影・王の嘆願の太刀・踊り子と鏡像)
  | 'greatblade'  // 幅広の黒い鋼 (処刑人の振り下ろし・斬り上げ・特大縦振り)
  | 'lance'       // 鋼の穂先と長い柄 (騎士の払い)
  | 'haft'        // 柄・石突き・蹴り = 鋼の鳴りが無い鈍い当たり
  | 'claw'        // 金の爪・冠・牙と獣の体の重み (戴冠獣・覚醒王・道化の鉤爪)
  | 'iron'        // 枷・鎖・車輪・砲身 = 重い鉄の塊
  | 'veil'        // 花嫁の薄衣と銀の閃き
  | 'shell'       // 砲の弾・発勁の衝撃波を刃で逸らす

export const PARRY_MATERIALS: readonly ParryMaterial[] = ['blade', 'greatblade', 'lance', 'haft', 'claw', 'iron', 'veil', 'shell']

export const PARRY_MATERIAL_LABELS: Record<ParryMaterial, string> = {
  blade: '刀・細い剣', greatblade: '幅広の黒い刃', lance: '槍の穂先', haft: '柄・蹴り', claw: '金の爪と牙',
  iron: '枷・鎖・車輪', veil: '薄衣と銀の閃き', shell: '砲の弾・衝撃波',
}

/** 材質そのものの重み (技の重さに足す)。鉄の塊・幅広の刃は同じ攻撃力でも重く、薄衣は軽く聞かせる。 */
export const MATERIAL_HEFT: Record<ParryMaterial, number> = {
  blade: 0, greatblade: 0.1, lance: 0.03, haft: 0, claw: 0.05, iron: 0.15, veil: -0.1, shell: 0,
}

/** 弾ける技すべての材質 (抜けは tests/parry_remake.test.ts が落とす)。heft = その技だけの足し引き。 */
export const PARRY_MATERIAL_BY_ATTACK: Record<string, { material: ParryMaterial; heft?: number }> = {
  // 亡霊 (教えの影)
  shadeSlash: { material: 'blade' }, shadeGuardChain: { material: 'blade' },
  // 戴冠獣 = 歯は無い。噛みは金の茨の冠、爪は金、前脚に金の枷と鎖、尾に金の輪と垂れた鎖
  houndBite: { material: 'claw' }, houndLunge: { material: 'claw' }, houndSwipe: { material: 'claw' },
  houndTripleSwipe1: { material: 'claw' }, houndTripleSwipe2: { material: 'claw' },
  houndTriple2Claw: { material: 'claw' }, houndTriple2Fang: { material: 'claw' },
  houndTripleSlam: { material: 'iron' }, houndTriple2Slam: { material: 'iron' }, houndTailSweep: { material: 'iron' },
  // 花嫁 = 薄衣の払いと銀の閃き
  veilSweep: { material: 'veil' }, veilSweepLink: { material: 'veil' }, veilBlinkTriple: { material: 'veil' }, veilFlurry: { material: 'veil' },
  // 夜番の騎士 = 鋼の槍。押し返しは柄
  lanceSweep: { material: 'lance' }, lanceCross: { material: 'lance' }, lanceShove: { material: 'haft' },
  executioner_phaseTransition: { material: 'shell' },
  // 請われぬ介錯 (今の定義) = 幅広の黒い刃・首枷・車輪・腕の砲 (四角い鋼の砲身)
  exec_maulDrop: { material: 'greatblade' }, exec_maulHold: { material: 'greatblade' }, exec_maulSet: { material: 'greatblade' },
  exec_cannonFajin: { material: 'shell' }, exec_bladeFajin: { material: 'shell' },
  exec_cannonShot: { material: 'shell' }, exec_cannonVolley: { material: 'shell' }, exec_cannonSweep: { material: 'iron' },
  // 覚醒王 = 金の牙・顎と一体の金の冠・金の爪・得物なし
  feralPocketSpin: { material: 'claw' }, feralBite: { material: 'claw' }, rampageV5: { material: 'claw' },
  feralFrenzy: { material: 'claw' }, feralFrenzyExtend: { material: 'claw' }, feralBiteDelayed: { material: 'claw' },
  // 道化の鉤爪・踊り子・鏡像
  dawnCallClawStudy: { material: 'claw' },
  tokiZurashiShort: { material: 'claw' }, tokiZurashiLong: { material: 'claw' }, clawCrowBreak: { material: 'claw' },
  tokiFrenzy0Short: { material: 'claw' }, tokiFrenzy0Long: { material: 'claw' }, tokiFrenzy1Short: { material: 'claw' }, tokiFrenzy1Long: { material: 'claw' },
  roosterAerialRoam: { material: 'claw' }, roosterClawPair: { material: 'claw' }, roosterCrowDrop: { material: 'claw' },
  roosterDawnCall: { material: 'haft' },  // 翼で打つ = 鋼の鳴りが無い鈍い当たり
  roosterFeather: { material: 'shell' },  // 落ちてくる羽を刃で逸らす
  waltzFlurry: { material: 'blade' }, crescendo: { material: 'blade' }, mirrorSlash: { material: 'blade' }, mirrorWaltz: { material: 'blade' },
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x))

/** 技の攻撃力 → 0〜1 (連続)。体幹ダメージを主 (7割)、HP ダメージを従 (3割)。
 *  体幹 12・HP 3 (いちばん軽い連撃の1発) = 0、体幹 60・HP 85 (処刑人の特大縦振り) = 1。 */
export function attackPower(postureDamage: number, damage: number): number {
  return clamp01(0.7 * (postureDamage - 12) / 48 + 0.3 * (damage - 3) / 82)
}

export type ParryMaterialHit = { material: ParryMaterial; weight: number }

/** 弾いた技 → 材質と重さ。技が分からない時 (開発の試し鳴らし等) は刀と、渡された重さ (無ければ 0.35)。 */
export function parryMaterialOf(attackId: string | undefined, fallbackWeight = 0.35): ParryMaterialHit {
  const atk = attackId ? ATTACKS[attackId] : undefined
  const entry = attackId ? PARRY_MATERIAL_BY_ATTACK[attackId] : undefined
  const material = entry?.material ?? 'blade'
  const base = atk ? attackPower(atk.postureDamage, atk.damage) : clamp01(fallbackWeight)
  return { material, weight: clamp01(base + MATERIAL_HEFT[material] + (entry?.heft ?? 0)) }
}
